<template>
    <BannerFooterOptions
        v-if="canManageMasterTemplates"
        :model-value="optionsVisible"
        :options="footerOptions"
        @update:modelValue="emit('updateOptionsVisible', $event)"
    />

    <MasterTemplateUploadButton
        ref="replace"
        class="hidden"
        :master-template-id="masterTemplate._id"
        @familyUpdate="emit('familyUpdate', $event)"
        @updated="emit('update')"
    />
</template>

<script lang="ts" setup>
import { computed, useTemplateRef } from "vue";
import { useRoute } from "vue-router";
import { Modal } from "view-ui-plus";
import { TemplateType } from "hoxton-shared/enums/masterTemplate";

import BannerFooterOptions from "@/components/Previews/BannerFooter/BannerFooterOptions.vue";
import MasterTemplateUploadButton from "@/components/Campaign/MasterTemplates/MasterTemplateUploadButton.vue";

import { apolloClient } from "@/apollo";
import QueryMasterTemplateDownloadUrl from "@/apollo/queries/MasterTemplateDownloadUrl.gql";

import type { MasterTemplate } from "@/types";
import * as auth from "@/services/Auth";
import downloadFile from "@/utils/downloadFile";

const route = useRoute();
const replace = useTemplateRef("replace");

const clientId = route.params.clientId as string;

const {
    resizeMode = undefined,
    optionsVisible,
    masterTemplate
} = defineProps<{
    resizeMode?: boolean;
    masterTemplate: MasterTemplate;
    optionsVisible: boolean;
}>();

const emit = defineEmits<{
    (e: "update"): void;
    (e: "familyUpdate", value: string[]): void;
    (e: "updateOptionsVisible", value: boolean): void;
    (e: "toggleResizeMode", value: boolean): void;
    (e: "remove"): void;
}>();

const canManageMasterTemplates = computed(() => auth.userCan(auth.Actions.CanManageMasterTemplates, { clientId }));

const isAE = computed(() => masterTemplate.adType === TemplateType.AE);
const isHTML5 = computed(() => masterTemplate.adType === TemplateType.HTML5);

const footerOptions = computed(() => {
    const options = [];

    if (isHTML5.value && resizeMode !== undefined) {
        options.push({
            id: "resize-button",
            label: resizeMode ? "Exit Resize Mode" : "Resize Mode",
            icon: "md-expand",
            onClick: toggleResizeMode
        });
    }

    if (!isAE.value) {
        options.push({
            id: "download-button",
            label: "Download Template",
            icon: "md-cloud-download",
            onClick: onDownload
        });

        options.push({
            id: "replace-button",
            label: "Replace Template",
            icon: "md-cloud-upload",
            onClick: onReplace
        });
    }

    options.push({
        id: "delete-button",
        label: "Delete",
        icon: "md-trash",
        onClick: deleteTemplatePrompt
    });

    return options;
});

function toggleResizeMode() {
    emit("toggleResizeMode", !resizeMode);
}

function hideFooterOptions() {
    emit("updateOptionsVisible", false);
}

async function onDownload() {
    const { data } = await apolloClient.query({
        query: QueryMasterTemplateDownloadUrl,
        variables: {
            masterTemplateId: masterTemplate._id
        }
    });

    downloadFile(data.masterTemplateDownloadUrl, `${masterTemplate.name}.zip`);

    hideFooterOptions();
}

async function onReplace() {
    hideFooterOptions();

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    replace.value.remoteClick();
}

function deleteTemplatePrompt() {
    hideFooterOptions();

    Modal.confirm({
        title: "Are you sure?",
        content:
            "This template may be used in other campaigns. All ads using this template will be deleted. This is permanent and can not be undone.",
        okText: "Delete",
        onOk: () => emit("remove"),
        cancelText: "Cancel",
        width: 300
    });
}
</script>
